import { put, putResolve, takeLatest } from "redux-saga/effects";

import {
  IAbortRunCodeAction,
  ICollabRunCodeAction,
  ICreateCodingSolutionArgs,
  IRunCodeArgs,
} from "@/types/codingProblems.types";
import { IInitialState } from "@/types/initialState.types";
import { IRunCodePayload } from "@/types/solutions.types";
import { showToast } from "@//actions/appStatus.action";
import { getSolutionByQuestionSlugAPI, runCodeAPI } from "@/api/solutions.api";
import {
  ABORT_RUN_CODE,
  COLLAB_RUN_CODE,
  CREATE_OR_UPDATE_CODING_SOLUTION,
  CUSTOM_INPUT_RUN_CODE,
  RUN_CODE,
  SET_ABORT_RUN_CODE_IN_PROCESS,
  SET_RUN_CODE_IN_PROCESS,
  TEST_CASE_RUN_CODE,
} from "@/constants/ActionTypes";
import { ESolutionStatus } from "@/types/questions.types";
import { encodeData } from "@/utils/encodeDecode.utils";
import { EToastType } from "@/components/common/AlertToast/AlertToast.types";
import { COLLAB_RUN_CODE_SUCCESS } from "@/constants/SocketEventConstants";
import {
  ERunCodeType,
  TSingleTestCase,
  TSingleTestCaseResult,
} from "@/components/common/RunCode/RunCode.types";
import apiErrorHandler from "@/utils/apiErrorHandler";
import {
  GET_SOLUTION_BY_QUESTION_SLUG_API_HTTP_STATUS_CODES,
  GET_SOLUTION_BY_QUESTION_SLUG_ERROR_MESSAGE,
} from "@/constants/Solutions";
import {
  RUN_CODE_API_HTTP_STATUS_CODES,
  RUN_CODE_ERROR_MESSAGE,
} from "@/constants/SolutionConstants";
import { call, select } from "./utils.saga";
import { createOrUpdateCondigSolution } from "./solutions.saga";

function* runCode({ payload }: IRunCodeArgs) {
  const { input, interviewSlug, runCodeType, questionSlug } = payload;
  try {
    const { question, user } = yield* select((state: IInitialState) => ({
      question: state.questionsDetails.questions[questionSlug],
      user: state.auth.userIdentityData,
    }));

    let solution = yield* select(
      (state: IInitialState) => state.solutions.allSolutions[questionSlug],
    );
    if (!solution.slug && user?.isInterviewer) {
      return;
    }
    yield put({
      type: SET_RUN_CODE_IN_PROCESS,
      payload: {
        questionSlug,
        inProcess: true,
      },
    });
    if (
      runCodeType &&
      interviewSlug &&
      solution?.code &&
      solution?.technology
    ) {
      if (!solution.slug) {
        const solutionPayload: ICreateCodingSolutionArgs = {
          type: null,
          payload: {
            interviewSlug,
            isRunCode: true,
            questionSlug,
          },
        };
        const solutionResp = yield* call(
          createOrUpdateCondigSolution,
          solutionPayload,
        );
        if (solutionResp && "slug" in solutionResp.data) {
          solution = solutionResp.data;
        }
      }
      if (solution?.slug) {
        const socket = yield* select(
          (state: IInitialState) => state.socket.socket,
        );
        const requestPayload: IRunCodePayload = {
          problem_type: question.question_type,
          input,
          sockmeta: {
            connid: "123456789",
            sockid: socket?.id ?? "",
            wid: "nimbus",
          },
          code: solution?.code,
          problem_slug: question.problem?.slug,
          evaluation_type:
            runCodeType === ERunCodeType.TESTCASES ? "all" : "none",
          technology: solution?.technology,
          solution_slug: solution.slug,
        };
        const runCodeResp = yield* call(
          runCodeAPI,
          interviewSlug,
          solution.slug,
          requestPayload,
        );
        solution.status = ESolutionStatus.RUNNING;
        yield put({
          type: `${CREATE_OR_UPDATE_CODING_SOLUTION}_SUCCESS`,
          payload: {
            questionSlug,
            solution,
          },
        });
        if (runCodeType === ERunCodeType.CUSTOM_INPUT) {
          yield put({
            type: `${CUSTOM_INPUT_RUN_CODE}_INITIATED`,
            payload: {
              solutionId: solution.id,
              sid: runCodeResp.data.sid,
            },
          });
          socket?.emit(
            COLLAB_RUN_CODE_SUCCESS,
            encodeData({
              questionSlug,
              runCodeType,
              sid: runCodeResp.data.sid,
            }),
          );
        } else if (question.problem) {
          const {
            non_sample_testcases: nonSampleTestCases,
            sample_testcases: sampleTestCases,
          } = question.problem;
          let testCasesResult: TSingleTestCase[] = [];
          if (typeof sampleTestCases === "object") {
            const sampleTestCasesArray = sampleTestCases
              .sort((testCaseA, testCaseB) =>
                testCaseA.name.localeCompare(testCaseB.name),
              )
              .map((testCase) => ({
                name: testCase.name,
                id: testCase.id,
                status: undefined,
              }));
            testCasesResult = [...sampleTestCasesArray];
          }

          if (typeof nonSampleTestCases === "object") {
            const nonSampleTestCasesArray = nonSampleTestCases
              .sort((testCaseA, testCaseB) =>
                testCaseA.name.localeCompare(testCaseB.name),
              )
              .map((testCase) => ({
                name: testCase.name,
                id: testCase.id,
                status: undefined,
              }));
            testCasesResult = [...testCasesResult, ...nonSampleTestCasesArray];
          }
          yield put({
            type: `${TEST_CASE_RUN_CODE}_INITIATED`,
            payload: {
              solutionId: solution.id,
              testCasesResult,
            },
          });
          socket?.emit(
            COLLAB_RUN_CODE_SUCCESS,
            encodeData({
              questionSlug,
              runCodeType,
              testCasesResult,
            }),
          );
        }
      }
    }
  } catch (error: unknown) {
    const message = yield* call(
      apiErrorHandler,
      error,
      RUN_CODE_API_HTTP_STATUS_CODES,
      RUN_CODE_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  } finally {
    yield put({
      type: SET_RUN_CODE_IN_PROCESS,
      payload: {
        questionSlug,
        inProcess: false,
      },
    });
  }
}

function* abortRuncode({ payload }: IAbortRunCodeAction) {
  const { questionSlug } = payload;
  yield put({
    type: SET_ABORT_RUN_CODE_IN_PROCESS,
    payload: {
      questionSlug,
      inProcess: true,
    },
  });
  const solution = yield* select(
    (state: IInitialState) => state.solutions.allSolutions[questionSlug],
  );
  const evaluationDetails = solution?.evaluation;
  let testCases: TSingleTestCaseResult[] = [];
  if (
    evaluationDetails?.testcases &&
    Object.keys(evaluationDetails.testcases).length
  ) {
    testCases = Object.values(evaluationDetails.testcases);
    testCases = testCases.sort((testcaseA, testcaseB) =>
      testcaseA.name.localeCompare(testcaseB.name),
    );
    testCases = testCases.sort((testCasesA) => (testCasesA.is_sample ? 1 : 0));
  }
  const abortPayload = {
    questionSlug,
    solutionId: solution?.id,
    testCases: testCases.length ? testCases : null,
    status: solution.evaluation?.status ?? null,
  };
  yield putResolve({
    type: `${ABORT_RUN_CODE}_SUCCESS`,
    payload: abortPayload,
  });
}

export function* collabRunCode({ payload }: ICollabRunCodeAction) {
  const { interviewSlug, runCodeType, questionSlug, sid, testCasesResult } =
    payload;
  try {
    const question = yield* select(
      (state: IInitialState) => state.questionsDetails.questions[questionSlug],
    );
    let solution = yield* select(
      (state: IInitialState) => state.solutions.allSolutions[questionSlug],
    );
    const solutionSlug = solution?.slug;
    if (!solutionSlug) {
      const solutionResp = yield* call(
        getSolutionByQuestionSlugAPI,
        interviewSlug,
        questionSlug,
      );
      solution = solutionResp.data;
    }
    solution.status = ESolutionStatus.RUNNING;
    yield put({
      type: `${CREATE_OR_UPDATE_CODING_SOLUTION}_SUCCESS`,
      payload: {
        questionSlug,
        solution,
      },
    });
    if (runCodeType === ERunCodeType.CUSTOM_INPUT) {
      yield put({
        type: `${CUSTOM_INPUT_RUN_CODE}_INITIATED`,
        payload: {
          solutionId: solution.id,
          sid,
        },
      });
    } else if (question.problem) {
      yield put({
        type: `${TEST_CASE_RUN_CODE}_INITIATED`,
        payload: {
          solutionId: solution.id,
          testCasesResult,
        },
      });
    }
  } catch (error) {
    const message = yield* call(
      apiErrorHandler,
      error,
      GET_SOLUTION_BY_QUESTION_SLUG_API_HTTP_STATUS_CODES,
      GET_SOLUTION_BY_QUESTION_SLUG_ERROR_MESSAGE,
    );
    if (message) yield put(showToast({ message, type: EToastType.ERROR }));
  } finally {
    yield put({
      type: SET_RUN_CODE_IN_PROCESS,
      payload: {
        questionSlug,
        inProcess: false,
      },
    });
  }
}

export default function* loadCodingProblemsSaga() {
  yield takeLatest(RUN_CODE, runCode);
  yield takeLatest(ABORT_RUN_CODE, abortRuncode);
  yield takeLatest(COLLAB_RUN_CODE, collabRunCode);
}
